import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Phone,
  Trophy,
  ArrowRight,
  Check,
  Globe2,
  MapPin,
} from "lucide-react";
import { MaskedLines, Reveal } from "@/components/motion/Reveal";
import { BrandMarquee } from "@/components/Marquee";
import { CTASection } from "@/components/CTASection";
import {
  BUSINESS,
  SERVICES,
  WHY_US,
  STATS,
  CHAPTERS,
  FAQS,
  PROCESS,
} from "@/data/content";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const Hero = () => {
  return (
    <section className="relative overflow-hidden bg-paper pb-20 pt-32 sm:pt-40">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-brand/25 blur-[130px]" />
        <div className="absolute -bottom-52 -left-40 h-[560px] w-[560px] rounded-full bg-ink/15 blur-[150px]" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand/40 to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
          <motion.img
            src="/walkoff-logo.png"
            alt=""
            aria-hidden="true"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 0.08, scale: 1 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="w-[860px] max-w-[90%] select-none"
          />
        </div>
      </div>
      <div className="relative mx-auto max-w-[1200px] px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap items-center gap-3"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-1.5">
            <span className="h-2 w-2 rounded-full bg-brand" />
            <span className="font-mono text-xs font-semibold uppercase tracking-[0.15em] text-ink/70">
              Louisiana Web Design & Growth Studio
            </span>
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand/10 px-4 py-1.5">
            <Globe2 className="h-3.5 w-3.5 text-brand-dark" />
            <span className="font-mono text-xs font-semibold uppercase tracking-[0.15em] text-brand-dark">
              100% Remote
            </span>
          </span>
        </motion.div>

        <h1 className="mt-8 max-w-5xl font-display text-[12vw] font-extrabold uppercase leading-[0.9] tracking-tightest text-ink sm:text-7xl lg:text-[6.2vw]">
          <MaskedLines lines={["Building your websites", "for today, and"]} delay={0.15} />
          <span className="block overflow-hidden">
            <motion.span
              className="block text-brand"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              the future.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.7 }}
          className="mt-8 max-w-2xl text-lg leading-relaxed text-ink/60"
        >
          We're {BUSINESS.name}—a Louisiana-based website company helping local businesses
          build a stronger online presence without the hassle of managing it themselves. We
          design, host, maintain, and update modern websites while providing practical digital
          tools and growth strategies that help businesses attract more customers.
          <span className="mt-4 block font-bold text-ink">
            Professional websites. Ongoing support. Built to win.
          </span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.72, duration: 0.7 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <Link
            to="/contact"
            data-testid="hero-quote-btn"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand px-8 py-4 font-bold text-ink transition-transform duration-300 hover:-translate-y-0.5 hover:bg-brand-dark hover:text-white"
          >
            Request a Free Quote
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <a
            href={BUSINESS.phoneHref}
            data-testid="hero-call-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-black/15 px-8 py-4 font-bold text-ink transition-colors hover:bg-white"
          >
            <Phone className="h-4 w-4 text-brand-dark" /> {BUSINESS.phone}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-black/10 pt-8"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-ink/60">
            <Trophy className="h-4 w-4 text-brand-dark" /> Websites built to win
          </div>
          <div className="flex items-center gap-2 text-sm font-semibold text-ink/60">
            <MapPin className="h-4 w-4 text-brand-dark" /> Serving all of Louisiana
          </div>
          <div className="flex items-center gap-2 text-sm font-semibold text-ink/60">
            <Globe2 className="h-4 w-4 text-brand-dark" /> Remote — work with us from anywhere
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const StatsBar = () => (
  <section className="border-y border-black/10 bg-white">
    <div className="mx-auto grid max-w-[1400px] grid-cols-2 divide-x divide-black/10 px-0 sm:grid-cols-4">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.05} className="px-6 py-10 sm:px-8">
          <div className="font-display text-4xl font-extrabold tracking-tight text-ink sm:text-5xl">
            {s.value}
          </div>
          <div className="mt-2 text-sm text-ink/55">{s.label}</div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Manifesto = () => (
  <section className="bg-white py-24 sm:py-32">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand">How we think</p>
        <h2 className="mt-6 max-w-3xl font-display text-4xl font-extrabold leading-[0.95] tracking-tight sm:text-6xl">
          We don't just build websites. We build growth.
        </h2>
      </Reveal>
      <div className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-black/10 bg-black/10 md:grid-cols-3">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.no} delay={i * 0.08} className="bg-white">
            <div className="flex h-full flex-col p-8 sm:p-10">
              <span className="font-mono text-sm font-bold text-brand">{c.no} /</span>
              <h3 className="mt-4 font-display text-2xl font-bold tracking-tight">{c.title}</h3>
              <p className="mt-4 text-[15px] leading-relaxed text-ink/60">{c.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ServicesPreview = () => (
  <section className="bg-paper py-24 sm:py-32">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand">What we do</p>
          <h2 className="mt-6 max-w-2xl font-display text-4xl font-extrabold leading-[0.95] tracking-tight sm:text-6xl">
            Everything your business needs to grow online.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link
            to="/services"
            data-testid="home-view-services"
            className="group inline-flex items-center gap-2 font-bold text-ink hover:text-brand"
          >
            View all services
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>

      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => (
          <Reveal key={s.title} delay={(i % 3) * 0.06}>
            <div className="group h-full rounded-2xl border border-black/10 bg-white p-8 transition-transform duration-300 hover:-translate-y-2 hover:border-brand">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand/15 text-brand-dark transition-colors duration-300 group-hover:bg-brand group-hover:text-ink">
                <s.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-6 font-display text-xl font-bold tracking-tight">{s.title}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-ink/60">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WhyUs = () => (
  <section className="relative overflow-hidden bg-ink py-24 text-white sm:py-32">
    <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand">Why choose us</p>
          <h2 className="mt-6 font-display text-4xl font-extrabold leading-[0.95] tracking-tight sm:text-5xl">
            Personable. Adaptive. Invested in your growth.
          </h2>
          <p className="mt-6 max-w-md text-white/60">
            We're not just here to build you a website — we're here to help your company
            grow in every aspect, and we'll adapt to exactly what you need.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3.5 font-bold text-ink transition-transform hover:-translate-y-0.5 hover:bg-brand-dark hover:text-white"
          >
            Start your project <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
      <div className="grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:col-span-8">
        {WHY_US.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.06} className="bg-ink">
            <div className="h-full p-8">
              <w.icon className="h-6 w-6 text-brand" />
              <h3 className="mt-5 font-display text-xl font-bold tracking-tight">{w.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/55">{w.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ProcessSection = () => (
  <section className="bg-white py-24 sm:py-32">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand">The process</p>
        <h2 className="mt-6 max-w-2xl font-display text-4xl font-extrabold leading-[0.95] tracking-tight sm:text-6xl">
          A clear path from idea to growth.
        </h2>
      </Reveal>
      <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {PROCESS.map((p, i) => (
          <Reveal key={p.no} delay={i * 0.08}>
            <div className="border-t-2 border-brand pt-6">
              <span className="font-mono text-sm font-bold text-brand">{p.no}</span>
              <h3 className="mt-3 font-display text-2xl font-bold tracking-tight">{p.title}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-ink/60">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ReviewsPreview = () => null;

const FaqSection = () => (
  <section className="bg-white py-24 sm:py-32">
    <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand">FAQ</p>
          <h2 className="mt-6 font-display text-4xl font-extrabold leading-[0.95] tracking-tight sm:text-5xl">
            Questions? We've got answers.
          </h2>
          <p className="mt-6 text-ink/60">
            Still curious? Call us at{" "}
            <a href={BUSINESS.phoneHref} className="font-bold text-brand">
              {BUSINESS.phone}
            </a>{" "}
            — we're happy to help.
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-8">
        <Reveal>
          <Accordion type="single" collapsible className="w-full" data-testid="home-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-black/10">
                <AccordionTrigger className="py-6 text-left font-display text-lg font-semibold tracking-tight hover:text-brand hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[15px] leading-relaxed text-ink/60">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <BrandMarquee />
      <StatsBar />
      <Manifesto />
      <ServicesPreview />
      <WhyUs />
      <ProcessSection />
      <FaqSection />
      <CTASection />
    </div>
  );
}
